import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { useOrganization } from "@/auth/organization";
import { RenewalRiskWarning } from "@/components/billing/SaveCardChoice";
import { CardBadge, PaymentMethodModal, defaultMethod, usePaymentMethods } from "@/components/domains/shared";
import { PoweredByStripe } from "@/components/billing/PoweredByStripe";
import {
  ErrorAlert,
  cx,
  Badge,
  Modal,
  Field,
  KeyValue,
  Button,
  buttonClass,
  LinkButton,
  Section,
  Toggle,
  useToast,
  type Requirement,
} from "@/components/ui";
import { attemptFinished, attemptKey } from "@/lib/attemptKey";
import {
  domainKeys,
  domainsApi,
  longDate,
  relativeDays,
  verificationApplies,
  type DomainDetail,
} from "@/lib/domains";
import { formatMoney } from "@/lib/format";
import { ApiError } from "@/lib/http";
import type { PaymentMethod } from "@/lib/types";
import { DomainIcon, TwoColumns, Notice, ConnectRow, QuickTile } from "@/components/domains/parts";

/** Figma "Domain Control Centre — Overview" (4073-6156; 2037-180 with a failed renewal). */
export function OverviewTab({ detail }: { detail: DomainDetail }) {
  const d = detail.domain;
  const { orgId, canOn } = useOrganization();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const methods = usePaymentMethods();
  const [renewOpen, setRenewOpen] = useState(false);
  const [renewCard, setRenewCard] = useState<PaymentMethod | null>(null);
  const [pickingCard, setPickingCard] = useState(false);
  const [years, setYears] = useState(1);
  const manage = canOn("domains:manage", "domain_folder", detail.folder?.id);
  // Actions stay visible to everyone who can open the domain; without domains:manage on its folder they
  // are unavailable and say why.
  const requires: Requirement = { permission: "domains:manage", on: ["domain_folder", detail.folder?.id] };
  const paymentOpen = params.get("modal") === "payment";
  const closeModal = () =>
    setParams((p) => {
      p.delete("modal");
      return p;
    });

  const refresh = () => qc.invalidateQueries({ queryKey: domainKeys.all(orgId) });
  // What a renewal costs a year (net) as the customer is shown it: a higher price they have not accepted yet comes first.
  const renewPrice = d.renewal_price_pending_cents ?? d.renewal_price_cents;
  // One hidden key per renewal being paid for (this domain, this many years): sending it twice pays once.
  const renewScope = `${d.id}:${years}`;
  const renew = useMutation({
    mutationFn: (methodId?: string) =>
      domainsApi.renew(orgId, d.id, years, methodId ?? null, attemptKey("renew", renewScope), renewPrice),
    onError: (err) => {
      attemptFinished("renew", renewScope, err);
      // The price moved since the page was opened: nothing was charged. Show the new price to accept.
      if (err instanceof ApiError && err.reason === "PRICE_CHANGED") void refresh();
    },
    onSuccess: async (order) => {
      attemptFinished("renew", renewScope);
      await refresh();
      setRenewOpen(false);
      setRenewCard(null);
      closeModal();
      if (order.status !== "completed") navigate(`/domains/orders/${order.id}`);
    },
  });
  const toast = useToast();
  const autoRenew = useMutation({
    mutationFn: (on: boolean) => domainsApi.update(orgId, d.id, { auto_renew: on }),
    onSuccess: async (_, on) => {
      await refresh();
      toast.show({ tone: "success", title: on ? "Auto-renew turned on" : "Auto-renew turned off" });
    },
  });
  const folder = useMutation({
    mutationFn: (id: string) => domainsApi.update(orgId, d.id, id ? { folder_id: id } : { unfile: true }),
    onSuccess: async () => {
      await refresh();
      toast.show({ tone: "success", title: "Folder updated" });
    },
  });

  const card = defaultMethod(methods.data?.payment_methods);
  const payWith = renewCard ?? card;
  const contact = detail.contact;
  const renewalFailed = d.health === "renewal_failed";
  const expiringManual = d.health === "expiring_soon" && !d.auto_renew;
  const daysLeft = d.days_left ?? 0;
  const ops = detail.operations.filter((o) => o.status === "pending");
  const lockPending = ops.some((o) => o.kind === "transfer_lock");

  return (
    <>
      <TwoColumns
        main={
          <>
            {renewalFailed && (
              <Notice
                tone="danger"
                title={d.renewal_retries_stopped ? "Automatic renewal has stopped" : "Renewal payment failed"}
                actions={
                  <>
                    <Button
                      size="lg"
                      variant="danger"
                      requires={requires}
                      onClick={() => setParams((p) => (p.set("modal", "payment"), p))}
                    >
                      Update payment method
                    </Button>
                    {d.renewal_retries_stopped && (
                      <Button
                        variant="brand"
                        size="lg"
                        requires={requires}
                        onClick={() => setRenewOpen(true)}
                      >
                        Renew now
                      </Button>
                    )}
                  </>
                }
              >
                {d.display_name} will stop working if payment isn't resolved before it expires{" "}
                {relativeDays(d.days_left)}. This is separate from your subscription payment.
                {d.renewal_failure ? ` The bank said: ${d.renewal_failure.replace(/\.$/, "")}.` : ""}
                {d.renewal_retries_stopped
                  ? ` We tried ${d.renewal_attempts} times and have stopped trying automatically. Renew it now to keep it. Our support team has been told.`
                  : ""}
              </Notice>
            )}
            {d.renewal_price_pending_cents && !renewalFailed && (
              <Notice
                tone="warning"
                title="Automatic renewal is paused: the price has gone up"
                actions={
                  <Button variant="brand" size="lg" requires={requires} onClick={() => setRenewOpen(true)}>
                    Review and renew
                  </Button>
                }
              >
                The yearly renewal price rose from {formatMoney(d.renewal_price_cents ?? 0, detail.currency)}{" "}
                to {formatMoney(d.renewal_price_pending_cents, detail.currency)} + VAT. We haven't charged
                you. Renew to accept the new price, or {d.display_name} expires on {longDate(d.expires_at)}.
              </Notice>
            )}
            {detail.operations.some((o) => o.status === "pending" && o.detail.delayed === true) && (
              <Notice tone="warning" title="A change is taking longer than expected">
                The registrar hasn't confirmed a recent change to {d.display_name} yet. Support has been told
                and is checking it. Nothing changes until the registrar confirms it.
              </Notice>
            )}
            {expiringManual && (
              <Notice
                tone="warning"
                title={`Expires ${relativeDays(d.days_left)} — auto-renew is off`}
                actions={
                  <Button variant="brand" size="lg" requires={requires} onClick={() => setRenewOpen(true)}>
                    Renew now
                  </Button>
                }
              >
                Renew it, or turn auto-renew on, to keep your website and email on {d.display_name} working.
              </Notice>
            )}
            {verificationApplies(contact, d.fqdn) &&
              contact?.verification === "pending" &&
              contact.verification_due_at && (
                <Notice
                  tone="danger"
                  title="Confirm your registrant email"
                  actions={
                    <Link to={`/domains/${d.id}?tab=contacts`} className={buttonClass("secondary", "lg")}>
                      Review contact
                    </Link>
                  }
                >
                  ICANN asks {contact.email} to confirm it by {longDate(contact.verification_due_at)}, or the
                  domain may be suspended.
                </Notice>
              )}

            <Section title="Connection">
              <ConnectionRow detail={detail} requires={requires} />
            </Section>

            <Section title="Quick actions">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <QuickTile
                  title="DNS records"
                  detail={
                    d.nameserver_mode === "custom"
                      ? "Managed elsewhere"
                      : `${detail.record_count} ${detail.record_count === 1 ? "record" : "records"}`
                  }
                  to={`/domains/${d.id}?tab=dns`}
                />
                <QuickTile
                  title="Nameservers"
                  detail={d.nameserver_mode === "custom" ? "Custom" : "CortexGrip default"}
                  to={`/domains/${d.id}?tab=nameservers`}
                />
                <QuickTile
                  title="Security"
                  detail={
                    lockPending ? "Lock change pending" : `Transfer lock ${d.transfer_lock ? "on" : "off"}`
                  }
                  to={`/domains/${d.id}?tab=security`}
                />
                <QuickTile
                  title="Transfer domain"
                  detail={d.transfer_lock ? "Not started" : "Unlocked"}
                  to={`/domains/${d.id}?tab=transfers`}
                />
              </div>
            </Section>

            <Section
              title="Renewal"
              subtitle={
                d.renewal_price_cents
                  ? `${formatMoney(d.renewal_price_cents, detail.currency)} a year + VAT, charged ${d.auto_renew ? "automatically 30 days before it expires" : "only when you renew"}`
                  : "Renews for one year at a time"
              }
              action={
                <Button size="lg" variant="secondary" requires={requires} onClick={() => setRenewOpen(true)}>
                  Renew now
                </Button>
              }
            >
              <div className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-fg">Auto-renew</p>
                  <p className="text-[13px] text-fg-subtle">
                    {d.auto_renew
                      ? `Renews on ${longDate(d.expires_at)} using ${card?.label ?? "your default card"}`
                      : `Expires on ${longDate(d.expires_at)} unless you renew it`}
                  </p>
                </div>
                <Toggle
                  size="lg"
                  checked={d.auto_renew}
                  label="Auto-renew"
                  requires={requires}
                  busy={autoRenew.isPending}
                  onChange={(on) => autoRenew.mutate(on)}
                />
              </div>
              <ErrorAlert error={autoRenew.error} />
            </Section>
          </>
        }
        side={
          <>
            <Section title="Domain details">
              <KeyValue variant="detail" label="Registered">
                <Badge size="md" tone="neutral">
                  {longDate(d.registered_at ?? d.created_at)}
                </Badge>
              </KeyValue>
              <KeyValue variant="detail" label={d.auto_renew ? "Renews" : "Expires"}>
                <Badge
                  size="md"
                  tone={
                    renewalFailed || (d.health === "expiring_soon" && daysLeft <= 7)
                      ? "danger"
                      : d.health === "expiring_soon"
                        ? "warning"
                        : "neutral"
                  }
                >
                  {longDate(d.expires_at)}
                </Badge>
              </KeyValue>
              <KeyValue variant="detail" label="Registrar">
                <Badge size="md" tone="neutral">
                  CortexGrip
                </Badge>
              </KeyValue>
              <KeyValue variant="detail" label="Auto-renew">
                <Badge size="md" tone={d.auto_renew ? "success" : "neutral"}>
                  {d.auto_renew ? "On" : "Off"}
                </Badge>
              </KeyValue>
              <KeyValue variant="detail" label="Transfer lock">
                <Badge size="md" tone={lockPending ? "warning" : d.transfer_lock ? "success" : "danger"}>
                  {lockPending ? "Updating" : d.transfer_lock ? "On" : "Off"}
                </Badge>
              </KeyValue>
              <FolderPicker
                detail={detail}
                editable={manage}
                busy={folder.isPending}
                onPick={(id) => folder.mutate(id)}
              />
            </Section>
            <section className="flex flex-col gap-3 rounded-[14px] border border-[#ffc4bb] bg-[#fff1ee] p-4 shadow-[0_6px_10px_rgba(26,20,18,0.08)]">
              <p className="text-base font-semibold text-fg">Transfer domain away</p>
              <p className="text-sm text-[#766e6a]">
                Move this domain to a different registrar. Your website and email may be affected until DNS is
                reconfigured.
              </p>
              <LinkButton
                to={`/domains/${d.id}?tab=transfers`}
                className={buttonClass("danger", "lg", "self-start")}
                requires={requires}
              >
                Start transfer out
              </LinkButton>
            </section>
          </>
        }
      />
      <Modal
        open={renewOpen && !pickingCard}
        onClose={() => setRenewOpen(false)}
        title={`Renew ${d.display_name}`}
        subtitle={`Adds time to the current expiry date (${longDate(d.expires_at)})`}
        note="Charged immediately upon confirmation."
        footer={
          <>
            <Button variant="secondary" size="lg" onClick={() => setRenewOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="brand"
              size="lg"
              loading={renew.isPending}
              disabledReason={payWith ? undefined : "Choose a card to pay with"}
              onClick={() => renew.mutate(payWith?.id)}
            >
              {renewPrice ? `Pay ${formatMoney(renewPrice * years, detail.currency)} + VAT` : "Renew"}
            </Button>
          </>
        }
      >
        <Field size="lg" label="Renew for">
          {({ id }) => (
            <select
              id={id}
              value={years}
              onChange={(e) => setYears(Number(e.target.value))}
              className={cx("w-full rounded-[10px] border border-border bg-surface p-3 text-sm")}
            >
              {Array.from({ length: 10 }, (_, i) => i + 1).map((y) => (
                <option key={y} value={y}>
                  {y} {y === 1 ? "year" : "years"}
                  {renewPrice ? ` — ${formatMoney(renewPrice * y, detail.currency)}` : ""}
                </option>
              ))}
            </select>
          )}
        </Field>
        <div className="flex items-center gap-3 rounded-xl bg-[#faf6ef] p-3">
          {payWith ? <CardBadge brand={payWith.brand} /> : <DomainIcon name="globe" size={24} />}
          <div className="min-w-0 flex-1">
            <p className="text-sm text-fg">{payWith ? payWith.label : "No card on file"}</p>
            {payWith && !payWith.saved && (
              <p className="text-xs text-fg-subtle">This payment only, not saved</p>
            )}
          </div>
          <button
            type="button"
            onClick={() => setPickingCard(true)}
            className="text-[13px] font-semibold text-brand-500 hover:underline"
          >
            {payWith ? "Change" : "Add a card"}
          </button>
        </div>
        {payWith && !payWith.saved && <RenewalRiskWarning />}
        <ErrorAlert error={renew.error} />
        <PoweredByStripe provider={methods.data?.provider} />
      </Modal>
      <PaymentMethodModal
        open={renewOpen && pickingCard}
        oneTime
        selectedId={payWith?.id}
        title={payWith ? "Change payment method" : "Add a payment method"}
        onClose={() => setPickingCard(false)}
        onChosen={(m) => {
          setRenewCard(m);
          setPickingCard(false);
        }}
      />
      <PaymentMethodModal
        open={paymentOpen}
        oneTime
        onClose={closeModal}
        onChosen={(m) => renew.mutate(m.id)}
        title="Update payment method"
      />
    </>
  );
}

function ConnectionRow({ detail, requires }: { detail: DomainDetail; requires: Requirement }) {
  const d = detail.domain;
  const linkIcon = <DomainIcon name="link" size={26} />;
  const dnsNote = d.nameserver_mode === "cortexgrip" ? "DNS managed by CortexGrip" : "DNS managed elsewhere";
  if (!d.site_id) {
    return (
      <ConnectRow
        first
        tile="neutral"
        icon={linkIcon}
        title="Not connected to a website"
        detail={`Point ${d.display_name} at your CortexGrip website · ${dnsNote}`}
        action={
          <LinkButton
            to={`/domains/${d.id}/connect`}
            className={buttonClass("brand", "lg")}
            requires={requires}
          >
            Connect
          </LinkButton>
        }
      />
    );
  }
  const connected = d.connection === "connected";
  const failed = d.connection === "error";
  return (
    <ConnectRow
      first
      tile={connected ? "success" : failed ? "danger" : "warning"}
      icon={linkIcon}
      title={
        connected
          ? `Connected to ${d.site?.name ?? "your website"}`
          : failed
            ? "Connection needs attention"
            : "Connecting to your website"
      }
      detail={`${d.is_primary ? "Primary custom domain" : "Custom domain"} · ${dnsNote}`}
      status={
        <Badge size="md" tone={connected ? "success" : failed ? "danger" : "warning"}>
          {connected ? "Connected" : failed ? "Needs fix" : "Configuring"}
        </Badge>
      }
      action={
        connected ? (
          <LinkButton
            to={`/domains/${d.id}/connect`}
            className={buttonClass("secondary", "lg")}
            requires={requires}
          >
            Change
          </LinkButton>
        ) : (
          <Link to={`/domains/${d.id}/connection`} className={buttonClass("secondary", "lg")}>
            View progress
          </Link>
        )
      }
    />
  );
}

/** The domain's folder: a picker for people who can move it, otherwise just its name. */
function FolderPicker({
  detail,
  editable,
  busy,
  onPick,
}: {
  detail: DomainDetail;
  editable: boolean;
  busy: boolean;
  onPick: (id: string) => void;
}) {
  const { orgId } = useOrganization();
  const folders = useQuery({
    queryKey: ["domains", orgId, "folders"],
    queryFn: () => domainsApi.folders(orgId),
  });
  if (!editable) {
    return (
      <KeyValue variant="detail" label="Folder">
        <Badge size="md" tone="neutral">
          {detail.folder?.name ?? "Unfiled"}
        </Badge>
      </KeyValue>
    );
  }
  return (
    <KeyValue variant="detail" label="Folder">
      <select
        aria-label="Folder"
        disabled={busy}
        aria-busy={busy}
        value={detail.domain.folder_id ?? ""}
        onChange={(e) => onPick(e.target.value)}
        className="rounded-full bg-[#f3ebe7] py-1 pr-2 pl-2.5 text-xs font-semibold text-[#5c5551] outline-none"
      >
        <option value="">Unfiled</option>
        {(folders.data?.folders ?? []).map((f) => (
          <option key={f.id} value={f.id}>
            {f.name}
          </option>
        ))}
      </select>
    </KeyValue>
  );
}
